import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FlaskConical, ShieldCheck, FileText, ArrowLeft, Clock, Timer, Gauge, Pill, Check, X, Sparkles, Cookie, Minus, Plus, Pencil, Leaf, Sun } from "lucide-react";

const STAFF_ROLES = ["admin", "owner", "manager", "employee"];

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const isStaff = STAFF_ROLES.includes(user?.role);
  const [p, setP] = useState(null);
  const [coa, setCoa] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/products/${slug}`)
      .then((r) => {
        setP(r.data);
        setActiveImg(0);
        const vs = r.data.variants || [];
        if (vs.length) {
          setSelectedVariant(vs.find((v) => v.in_stock !== false) || vs[0]);
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  // Load the Certificate of Analysis for the selected flavor's batch (falls back to the product batch).
  useEffect(() => {
    const batch = (selectedVariant?.batch || "").trim() || p?.batch_number;
    if (!batch) { setCoa(null); return; }
    api.get("/coas", { params: { batch } })
      .then((cr) => setCoa((cr.data || []).find((c) => c.batch_number === batch) || cr.data?.[0] || null))
      .catch(() => setCoa(null));
  }, [selectedVariant, p]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto py-24 px-6 text-center">
        <h1 className="font-serif text-4xl text-navy">Product not found</h1>
        <Link to="/shop" className="mt-6 inline-block text-copper underline">Back to shop</Link>
      </div>
    );
  }
  if (!p) return <div className="max-w-3xl mx-auto py-24 px-6 text-center text-charcoal/70">Loading…</div>;

  const variants = p.variants || [];
  const images = [p.image_url, ...(p.gallery || [])].filter(Boolean);
  const cover = images[activeImg] || images[0] || "";
  const hasVar = variants.length > 0;
  const soldOut = hasVar && (!selectedVariant || selectedVariant.in_stock === false);
  const displayPrice = hasVar
    ? (selectedVariant ? `$${(Number(selectedVariant.price) || 0).toFixed(2)}` : "Select an option below")
    : (p.price_display || "Contact for pricing");

  const doAdd = () => {
    if (soldOut) { toast.error("Please choose an available option."); return; }
    addItem(p, qty, hasVar ? selectedVariant : null);
  };

  return (
    <div data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-6 flex items-center justify-between">
        <Link to="/shop" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-sand/70 hover:text-copper">
          <ArrowLeft size={14}/> Back to shop
        </Link>
        {isStaff && (
          <Link to={`/admin/products?edit=${p.slug}`} data-testid="pd-edit-product" className="inline-flex items-center gap-2 bg-copper text-navy px-4 py-2 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C158] transition-colors">
            <Pencil size={13}/> Edit Product
          </Link>
        )}
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 pb-16">
        <div>
          <div className="aspect-[4/5] overflow-hidden bg-white">
            {cover && <img src={mediaUrl(cover)} alt={p.name} className="w-full h-full object-contain p-4" />}
          </div>
          {images.length > 1 && (
            <div data-testid="product-gallery-thumbs" className="mt-3 flex flex-wrap gap-2">
              {images.map((src, i) => (
                <button
                  key={i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`w-16 h-16 bg-white overflow-hidden border-2 transition-colors ${i === activeImg ? "border-copper" : "border-transparent hover:border-sand/50"}`}
                >
                  <img src={mediaUrl(src)} alt="" className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:pt-4">
          <span className="eyebrow">Premium Hemp</span>
          <h1 className="font-serif text-4xl sm:text-5xl text-sand mt-4 leading-[1.05] flex items-start gap-3 flex-wrap">
            <span>{p.name}</span>
            {p.is_new && (
              <span data-testid="pd-new-badge" className="bg-copper text-navy px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] leading-none self-center">New</span>
            )}
          </h1>
          <p className="mt-5 text-lg text-sand/80 leading-relaxed font-light">{p.short_description}</p>
          <div className="mt-7 font-serif text-3xl text-copper">{displayPrice}</div>
          {soldOut && <div className="mt-2 text-sm text-texasred uppercase tracking-[0.15em]">Currently sold out</div>}

          {hasVar && (
            <div data-testid="variant-picker" className="mt-6">
              <div className="text-[11px] uppercase tracking-[0.22em] text-sand/70 mb-3">Choose an option</div>
              <div className="flex flex-wrap gap-2.5">
                {variants.map((v) => {
                  const oos = v.in_stock === false;
                  const active = selectedVariant?.id === v.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      data-testid={`variant-${v.id}`}
                      disabled={oos}
                      onClick={() => setSelectedVariant(v)}
                      className={`px-4 py-3 border text-sm transition-colors text-left ${
                        oos
                          ? "border-tan/30 text-sand/40 line-through cursor-not-allowed"
                          : active
                          ? "border-copper bg-copper text-navy font-semibold"
                          : "border-tan/60 text-sand hover:border-copper"
                      }`}
                    >
                      <span className="block">{v.label}</span>
                      <span className={`block text-xs mt-0.5 ${active ? "text-navy/80" : "text-copper"}`}>
                        ${(Number(v.price) || 0).toFixed(2)}{oos ? " · Sold out" : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="flex items-center border border-tan/60 bg-white">
              <button data-testid="pd-qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-3.5 text-navy hover:bg-sand"><Minus size={16} /></button>
              <span data-testid="pd-qty" className="px-4 text-navy w-10 text-center">{qty}</span>
              <button data-testid="pd-qty-inc" onClick={() => setQty((q) => q + 1)} className="px-3 py-3.5 text-navy hover:bg-sand"><Plus size={16} /></button>
            </div>
            <button
              data-testid="add-to-cart-btn"
              disabled={soldOut}
              onClick={() => { doAdd(); if (!soldOut) toast.success(`${qty}× ${p.name}${selectedVariant ? " — " + selectedVariant.label : ""} added to cart`); }}
              className="bg-copper text-navy font-semibold px-8 py-4 text-sm tracking-[0.2em] uppercase hover:bg-[#E5C158] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add to Cart
            </button>
            <button
              data-testid="buy-now-btn"
              disabled={soldOut}
              onClick={() => { if (soldOut) { toast.error("Please choose an available option."); return; } doAdd(); navigate("/cart"); }}
              className="border border-copper/50 text-sand px-8 py-4 text-sm tracking-[0.2em] uppercase hover:bg-copper hover:text-navy transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Buy Now
            </button>
          </div>

          {/* AT A GLANCE — quick-decision panel */}
          {(p.effects?.length || p.onset || p.duration || p.dose_guidance || p.dosage || p.strength || p.flavor || p.plant_type || p.time_of_day || p.cannabinoids?.length) ? (
            <div data-testid="at-a-glance" className="mt-10 bg-sand border border-tan/50 p-6">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-copper" strokeWidth={1.5} />
                <span className="eyebrow">At a Glance</span>
              </div>

              {/* Cannabinoid tags */}
              {p.cannabinoids?.length > 0 && (
                <div data-testid="pd-cannabinoids" className="mt-4 flex flex-wrap gap-2">
                  {p.cannabinoids.map((c) => (
                    <span key={c} className="border border-copper text-copper px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]">
                      {c}
                    </span>
                  ))}
                </div>
              )}

              {/* Effects pills */}
              {p.effects?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {p.effects.map((e) => (
                    <span key={e} className="bg-navy text-white px-3 py-1 text-[11px] uppercase tracking-[0.18em]">
                      {e}
                    </span>
                  ))}
                </div>
              )}

              {/* Quick facts grid */}
              <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-4">
                {p.onset && (
                  <Fact icon={Clock} label="Onset" value={p.onset} />
                )}
                {p.duration && (
                  <Fact icon={Timer} label="Duration" value={p.duration} />
                )}
                {p.strength && (
                  <Fact icon={Gauge} label="Strength" value={p.strength} />
                )}
                {p.plant_type && (
                  <Fact icon={Leaf} label="Type" value={p.plant_type} />
                )}
                {p.time_of_day && (
                  <Fact icon={Sun} label="Best time" value={p.time_of_day} />
                )}
                {p.flavor && (
                  <Fact icon={Cookie} label="Flavor" value={p.flavor} />
                )}
                {p.dosage && (
                  <div className="col-span-2 md:col-span-3">
                    <Fact icon={Pill} label="Dosage per gummy/container" value={p.dosage} />
                  </div>
                )}
                {p.dose_guidance && (
                  <div className="col-span-2 md:col-span-3">
                    <Fact icon={Pill} label="How to start" value={p.dose_guidance} />
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* Good for / Not great for */}
          {(p.best_for?.length || p.not_for?.length) ? (
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              {p.best_for?.length > 0 && (
                <div data-testid="best-for" className="bg-white border border-tan/40 p-5">
                  <div className="flex items-center gap-2">
                    <Check size={16} className="text-[#2f6f3a]" strokeWidth={2}/>
                    <span className="text-[10px] uppercase tracking-[0.22em] text-charcoal/85 font-medium">Good for</span>
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {p.best_for.map((b) => (
                      <li key={b} className="text-sm text-charcoal/90 leading-relaxed">— {b}</li>
                    ))}
                  </ul>
                </div>
              )}
              {p.not_for?.length > 0 && (
                <div data-testid="not-for" className="bg-white border border-tan/40 p-5">
                  <div className="flex items-center gap-2">
                    <X size={16} className="text-texasred" strokeWidth={2}/>
                    <span className="text-[10px] uppercase tracking-[0.22em] text-charcoal/85 font-medium">Not great for</span>
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {p.not_for.map((n) => (
                      <li key={n} className="text-sm text-charcoal/90 leading-relaxed">— {n}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}

          {p.cannabinoid_profile?.length > 0 && (
            <div className="mt-10">
              <div className="eyebrow">Cannabinoid Profile</div>
              <div className="mt-3 border-t border-copper/20">
                {p.cannabinoid_profile.map((c, i) => (
                  <div key={i} className="flex justify-between py-3 border-b border-copper/15">
                    <span className="text-sand/80">{c.name}</span>
                    <span className="font-medium text-copper">{c.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {p.ingredients?.length > 0 && (
            <div className="mt-8">
              <div className="eyebrow">Ingredients</div>
              <p className="mt-3 text-sand/85 leading-relaxed">{p.ingredients.join(" • ")}</p>
            </div>
          )}

          {p.usage && (
            <div className="mt-8">
              <div className="eyebrow">How to Use</div>
              <p className="mt-3 text-sand/85 leading-relaxed">{p.usage}</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-sand/95 border-y border-copper/25">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white border border-copper/20 p-7 flex gap-4">
            <FlaskConical size={28} className="text-copper flex-shrink-0" strokeWidth={1.25} />
            <div>
              <div className="font-serif text-xl text-navy">Lab Tested</div>
              <p className="text-sm text-charcoal/85 mt-2 leading-relaxed">Batch {coa?.batch_number || selectedVariant?.batch || p.batch_number || "—"}. {coa ? `Tested by ${coa.lab_name}.` : "COA available on request."}</p>
              {coa && (
                <Link to={`/lab-reports?batch=${coa.batch_number}`} className="inline-flex gap-1 items-center mt-3 text-sm text-copper hover:text-texasred">
                  <FileText size={14}/> View Certificate of Analysis
                </Link>
              )}
            </div>
          </div>
          <div className="bg-white border border-copper/20 p-7 flex gap-4">
            <ShieldCheck size={28} className="text-copper flex-shrink-0" strokeWidth={1.25} />
            <div>
              <div className="font-serif text-xl text-navy">Farm Bill Compliant</div>
              <p className="text-sm text-charcoal/85 mt-2 leading-relaxed">Less than 0.3% Delta-9 THC by dry weight, per the 2018 U.S. Farm Bill.</p>
            </div>
          </div>
          <div className="bg-white border border-copper/20 p-7 flex gap-4">
            <FileText size={28} className="text-copper flex-shrink-0" strokeWidth={1.25} />
            <div>
              <div className="font-serif text-xl text-navy">Transparent</div>
              <p className="text-sm text-charcoal/85 mt-2 leading-relaxed">Sourced from licensed U.S. farms. Full supply chain on file.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <span className="eyebrow">Description</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-sand mt-4">About this product</h2>
          <div className="divider my-5" />
          <p className="text-sand/85 leading-relaxed text-lg font-light">{p.description}</p>

          {p.faqs?.length > 0 && (
            <div className="mt-16">
              <span className="eyebrow">FAQs</span>
              <h2 className="font-serif text-3xl sm:text-4xl text-sand mt-4">Frequently asked questions</h2>
              <div className="divider my-5" />
              <Accordion type="single" collapsible className="w-full mt-2">
                {p.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-copper/20">
                    <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-base text-sand hover:text-copper">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sand/75 leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>
        <aside className="lg:col-span-5">
          <div className="bg-navy border border-copper/25 text-sand p-8">
            <div className="eyebrow">Required Notice</div>
            <p className="mt-4 text-sm leading-relaxed text-sand/80">
              These statements have not been evaluated by the Food and Drug Administration.
              This product is not intended to diagnose, treat, cure, or prevent any disease.
              Use responsibly. 21+ only.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={18} className="text-copper flex-shrink-0 mt-0.5" strokeWidth={1.5} />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/65">{label}</div>
        <div className="text-sm text-navy mt-0.5 leading-snug">{value}</div>
      </div>
    </div>
  );
}
